"use strict";
import { ChartType, PriceChartType } from "components/Charts/utils";
import {
  DataQuality,
  checkDataQuality,
  withUTCTimestamp
} from "components/Tokens/TokenDetails/ChartSection/util";
import { useMemo, useReducer } from "react";
import {
  useTokenHistoricalTvlsQuery,
  useTokenHistoricalVolumesQuery,
  useTokenPriceQuery
} from "uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks";
function fallbackToPriceChartData(priceHistoryEntry) {
  const { value, timestamp } = priceHistoryEntry;
  const time = timestamp;
  return { time, value, open: value, high: value, low: value, close: value };
}
function toPriceChartData(ohlc) {
  const { open, high, low, close } = ohlc;
  const time = ohlc.timestamp;
  return { time, value: close.value, open: open.value, high: high.value, low: low.value, close: close.value };
}
const currentTimeSeconds = () => Date.now() / 1e3;
const CANDLESTICK_FALLBACK_THRESHOLD = 0.1;
export function useTDPPriceChartData({
  variables,
  skip,
  priceChartType
}) {
  const [fallback, enablePriceHistoryFallback] = useReducer(() => true, false);
  const { data, loading } = useTokenPriceQuery({ variables: { ...variables, fallback }, skip });
  return useMemo(() => {
    const { ohlc, priceHistory, price } = data?.token?.market ?? {};
    let entries = (ohlc ? ohlc.filter((v) => v !== void 0).map(toPriceChartData) : priceHistory?.filter((v) => v !== void 0).map(fallbackToPriceChartData)) ?? [];
    const currentPrice = price?.value;
    if (ohlc) {
      const zeroCount = entries.filter((x) => x.value === 0).length;
      if (!ohlc.length || zeroCount / entries.length > CANDLESTICK_FALLBACK_THRESHOLD) {
        enablePriceHistoryFallback();
        return {
          chartType: ChartType.PRICE,
          entries: [],
          loading: true,
          disableCandlestickUI: true,
          dataQuality: DataQuality.INVALID
        };
      }
      if (priceChartType === PriceChartType.LINE) {
        let min = entries[0].low;
        let minIndex = 0;
        let max = entries[0].high;
        let maxIndex = 0;
        entries.forEach((entry, index) => {
          if (entry.low < min) {
            min = entry.low;
            minIndex = index;
          }
          if (entry.high > max) {
            max = entry.high;
            maxIndex = index;
          }
        });
        if (minIndex !== entries.length - 1) {
          entries[minIndex].value = min;
        }
        if (maxIndex !== entries.length - 1) {
          entries[maxIndex].value = max;
        }
      } else if (priceChartType === PriceChartType.CANDLESTICK) {
        const combinedEntries = [];
        const startIndex = entries.length % 2;
        for (let i = startIndex; i < entries.length; i += 2) {
          const first = entries[i];
          const second = entries[i + 1];
          const combined = {
            time: first.time,
            open: first.open,
            high: Math.max(first.high, second.high),
            low: Math.min(first.low, second.low),
            close: second.close,
            value: second.close
          };
          combinedEntries.push(combined);
        }
        entries = combinedEntries;
      }
    }
    if (currentPrice && entries.length > 1) {
      const lastEntry = entries[entries.length - 1];
      const secondToLastEntry = entries[entries.length - 2];
      const granularity = lastEntry.time - secondToLastEntry.time;
      const time = currentTimeSeconds();
      if (time - lastEntry.time < granularity) {
        lastEntry.time = time;
        lastEntry.value = currentPrice;
        lastEntry.close = currentPrice;
      } else {
        entries.push({
          time,
          value: currentPrice,
          open: currentPrice,
          high: currentPrice,
          low: currentPrice,
          close: currentPrice
        });
      }
    }
    const dataQuality = checkDataQuality({ data: entries, chartType: ChartType.PRICE, duration: variables.duration });
    return { chartType: ChartType.PRICE, entries, loading, dataQuality, disableCandlestickUI: fallback };
  }, [data?.token?.market, fallback, loading, priceChartType, variables.duration]);
}
export function useTDPVolumeChartData(variables, skip) {
  const { data, loading } = useTokenHistoricalVolumesQuery({ variables, skip });
  return useMemo(() => {
    const entries = data?.token?.market?.historicalVolume?.filter((v) => v !== void 0).map(withUTCTimestamp) ?? [];
    const dataQuality = checkDataQuality({ data: entries, chartType: ChartType.VOLUME, duration: variables.duration });
    return { chartType: ChartType.VOLUME, entries, loading, dataQuality };
  }, [data?.token?.market?.historicalVolume, loading, variables.duration]);
}
function toStackedLineData(entry) {
  return { values: [entry.value], time: entry.timestamp };
}
export function useTDPTVLChartData(variables, skip) {
  const { data, loading } = useTokenHistoricalTvlsQuery({ variables, skip });
  return useMemo(() => {
    const { historicalTvl, totalValueLocked } = data?.token?.market ?? {};
    const entries = historicalTvl?.filter((v) => v !== void 0).map(toStackedLineData) ?? [];
    const currentTvl = totalValueLocked?.value;
    if (currentTvl && entries.length > 1) {
      const lastEntry = entries[entries.length - 1];
      const secondToLastEntry = entries[entries.length - 2];
      const granularity = lastEntry.time - secondToLastEntry.time;
      const time = currentTimeSeconds();
      if (time - lastEntry.time < granularity) {
        lastEntry.time = time;
        lastEntry.values = [currentTvl];
      } else {
        entries.push({ time, values: [currentTvl] });
      }
    }
    const dataQuality = checkDataQuality({ data: entries, chartType: ChartType.TVL, duration: variables.duration });
    return { chartType: ChartType.TVL, entries, loading, dataQuality };
  }, [data?.token?.market, loading, variables.duration]);
}
